// Pure helper — maps a numeric value to a Tailwind text-colour class via the
// `colorToken` of the matching tier in the given threshold set. Used by
// EpicCompletionCard's "Stories & Tasks created … without epic" line and by
// the Workflow Hygiene cards (PR 5 / US4) so both surfaces classify the same
// value through the same source of truth.
//
// Returns a neutral class when the set is missing (definitions still loading)
// or when no tier matches a possible-but-unlikely malformed set — preferring
// a transient neutral over a hard-coded default that would flash the wrong
// colour during cold load.

import type { IMetricThresholdSet, MetricTierColor } from '@skystone/shared';
import { classify } from '@skystone/shared';

export const NEUTRAL_TIER_COLOR_CLASS = 'text-foreground';

const TIER_COLOR_CLASS: Record<MetricTierColor, string> = {
  success: 'text-success',
  primary: 'text-primary',
  warning: 'text-warning',
  error: 'text-error',
};

export function getTierColorClass(
  value: number,
  thresholdSet: IMetricThresholdSet | null | undefined,
): string {
  if (!thresholdSet || thresholdSet.tiers.length === 0) return NEUTRAL_TIER_COLOR_CLASS;
  const tierId = classify(value, thresholdSet);
  if (!tierId) return NEUTRAL_TIER_COLOR_CLASS;
  const tier = thresholdSet.tiers.find((t) => t.tierId === tierId);
  // Last `?? NEUTRAL_TIER_COLOR_CLASS`: defends against a runtime tier whose
  // `colorToken` is not one of the four expected MetricTierColor values
  // (e.g. malformed admin edit, schema drift). Without this guard the
  // dictionary lookup would yield `undefined` and silently drop the class.
  return tier ? (TIER_COLOR_CLASS[tier.colorToken] ?? NEUTRAL_TIER_COLOR_CLASS) : NEUTRAL_TIER_COLOR_CLASS;
}
